import React from "react";
import {
  AbsoluteFill, useCurrentFrame, useVideoConfig,
  spring, interpolate, Audio, Img, staticFile,
} from "remotion";
import {z} from "zod";
import {TransitionSeries, linearTiming} from "@remotion/transitions";
// import {wipe} from "@remotion/transitions/wipe"; // reserved for future use
import {fade} from "@remotion/transitions/fade";
import {slide} from "@remotion/transitions/slide";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {SceneBackground} from "../components/backgrounds/SceneBackground";
import {KineticText} from "../components/text/KineticText";
import {GlowText} from "../components/dimus/GlowText";
import {AccentLine} from "../components/dimus/AccentLine";
import {AudioTrack} from "../components/media/AudioTrack";
import {SERIES_BGM, scenesToDuckSegments} from "../presets/music";

/**
 * CafeReel — Template parametrizável para linha "café_roi"
 *
 * Herda DNA visual de CafeEp11Rich.tsx (dia 31).
 * Cada tipo de cena tem UM elemento hero — zero competição visual.
 *
 * Tipos de cena:
 *   hook        — floating-shapes + pill + KineticText grande
 *   concept     — grid-dots + pill + KineticText + contraste
 *   stat        — gradient-drift + GlowText hero (número) + label
 *   compare     — gradient-drift + 2 CompareCards
 *   list        — dark-void + bullets com glow dot
 *   commitment  — dark-void + pill + texto left-aligned
 *   payoff      — warm-glow + pill + KineticText + subtítulo
 *   cta         — warm-glow + pergunta + handle Guilherme
 */

// ── Cores da linha editorial ────────────────────────────────────────────────
const ACCENT  = IDV.third;    // amarelo — cor primária do café
const GREEN   = IDV.secondary;
const WHITE   = "#f5f5f7";
const MUTED   = "#a3a3a3";
const SZ      = SAFE_ZONES.universal;
const TRANS   = 20; // frames de transição

// ── Schema de cena ──────────────────────────────────────────────────────────
const baseScene = {
  audio:     z.string(),
  durFrames: z.number(),
  pill:      z.string().optional(),
};

export const cafeSceneSchema = z.discriminatedUnion("type", [
  z.object({ ...baseScene, type: z.literal("hook"),
    title: z.string(), highlightWords: z.array(z.string()).optional() }),

  z.object({ ...baseScene, type: z.literal("concept"),
    title: z.string(), highlightWords: z.array(z.string()).optional(),
    contrastText: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("stat"),
    stat: z.string(), statLabel: z.string(), subtext: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("compare"),
    labelA: z.string(), subtitleA: z.string(), iconA: z.string(),
    labelB: z.string(), subtitleB: z.string(), iconB: z.string() }),

  z.object({ ...baseScene, type: z.literal("list"),
    title: z.string(), highlightWords: z.array(z.string()).optional(),
    bullets: z.array(z.string()) }),

  z.object({ ...baseScene, type: z.literal("commitment"),
    title: z.string(), body: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("payoff"),
    title: z.string(), highlightWords: z.array(z.string()).optional(),
    subtitle: z.string().optional() }),

  z.object({ ...baseScene, type: z.literal("cta"),
    question: z.string() }),
]);

export type CafeScene = z.infer<typeof cafeSceneSchema>;

export const cafeReelSchema = z.object({
  scenes:    z.array(cafeSceneSchema).min(1),
  bgm:       z.string().default("music/cafe-lofi.mp3"),
  bgmVolume: z.number().default(0.12),
  ttsVolume: z.number().default(0.95),
});

// ── Shared components ───────────────────────────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay?: number; icon?: string}> = ({text, color, delay = 0, icon}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p, transform: `scale(${interpolate(p,[0,1],[0.7,1],CLAMP)})`,
      padding: "10px 22px", borderRadius: 999, display:"flex", alignItems:"center", gap: 8,
      background: `${color}18`, border: `1.5px solid ${color}44`,
      backdropFilter: "blur(8px)",
    }}>
      {icon && <span style={{fontSize: 18}}>{icon}</span>}
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK, letterSpacing: 1.5}}>{text}</span>
    </div>
  );
};

const CompareCard: React.FC<{
  title: string; subtitle: string; color: string; icon: string; delay: number; highlight?: boolean;
}> = ({title, subtitle, color, icon, delay, highlight = false}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.heavy});
  const glow = highlight && frame > delay + 20 ? 1 + Math.sin(frame * 0.06) * 0.03 : 1;
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p, borderRadius: 20, width: "90%", padding: "28px 24px",
      transform: `translateY(${interpolate(p,[0,1],[40,0],CLAMP)}px) scale(${glow})`,
      background: highlight ? `${color}12` : "rgba(255,255,255,0.03)",
      border: `2px solid ${highlight ? `${color}66` : "rgba(255,255,255,0.06)"}`,
      boxShadow: highlight ? `0 0 30px ${color}20` : "none",
      backdropFilter: "blur(4px)",
      display:"flex", flexDirection:"column", alignItems:"center", gap: 10,
    }}>
      <div style={{fontSize: 44}}>{icon}</div>
      <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color, fontFamily: FONT_STACK, textAlign:"center"}}>{title}</div>
      <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign:"center", lineHeight: 1.3}}>{subtitle}</div>
    </div>
  );
};

// ── Bullet item com glow dot ────────────────────────────────────────────────
const BulletItem: React.FC<{text: string; delay: number; accent: string}> = ({text, delay, accent}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      display:"flex", alignItems:"flex-start", gap: 18,
      opacity: p, transform: `translateX(${interpolate(p,[0,1],[-24,0],CLAMP)}px)`,
    }}>
      <div style={{
        width: 10, height: 10, borderRadius:"50%", background: accent,
        marginTop: 12, flexShrink: 0, boxShadow: `0 0 10px ${accent}99`,
      }}/>
      <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: FONT_SCALE.body.min, fontWeight: 600, lineHeight: 1.3}}>
        {text}
      </div>
    </div>
  );
};

// ── Scene renderers ─────────────────────────────────────────────────────────

const SceneHook: React.FC<{s: Extract<CafeScene,{type:"hook"}>; ttsVol: number}> = ({s, ttsVol}) => (
  <AbsoluteFill>
    <SceneBackground mode="floating-shapes" accent={ACCENT} accent2={GREEN} seed="cafe-hook"/>
    <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
      gap: 24, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(s.audio)} volume={ttsVol}/>
      <Pill text={s.pill ?? "☕ CAFÉ COM TEU ROI"} color={ACCENT} delay={5} icon="☕"/>
      <KineticText
        text={s.title} mode="stagger-blur" delay={15}
        fontSize={FONT_SCALE.h1.max} fontWeight={900} color={WHITE}
        highlightWords={s.highlightWords ?? []} highlightColor={ACCENT}
        textAlign="center" lineHeight={1.15} maxWidth="90%"
      />
      <AccentLine color={ACCENT} delay={40} width={200}/>
    </AbsoluteFill>
  </AbsoluteFill>
);

const SceneConcept: React.FC<{s: Extract<CafeScene,{type:"concept"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const contrastP = spring({frame: frame - 70, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="grid-dots" accent={ACCENT}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
        gap: 28, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        {s.pill && <Pill text={s.pill} color={ACCENT} delay={5} icon="💡"/>}
        <KineticText
          text={s.title} mode="stagger-up" delay={15}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightWords={s.highlightWords ?? []} highlightColor={ACCENT}
          textAlign="center" lineHeight={1.2} maxWidth="92%"
        />
        {s.contrastText && (
          <div style={{
            opacity: contrastP,
            transform: `translateY(${interpolate(contrastP,[0,1],[16,0],CLAMP)}px)`,
            padding: "24px 32px", borderRadius: 16,
            background: `${ACCENT}10`, border: `1.5px solid ${ACCENT}35`,
            backdropFilter: "blur(6px)",
          }}>
            <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: FONT_SCALE.body.min, fontWeight: 600, lineHeight: 1.4, textAlign:"center"}}>
              {s.contrastText}
            </div>
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneStat: React.FC<{s: Extract<CafeScene,{type:"stat"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const labelP = spring({frame: frame - 40, fps, config: SPRINGS.smooth});
  const subtextP = spring({frame: frame - 80, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" accent={ACCENT} accent2={IDV.primary}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
        gap: 16, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        {/* Hero stat — ÚNICO elemento dominante */}
        <GlowText text={s.stat} delay={0} size={FONT_SCALE.hero.max} color={ACCENT}/>
        <div style={{
          opacity: labelP, fontFamily: FONT_STACK, color: MUTED, letterSpacing: 3,
          fontSize: FONT_SCALE.label.min, fontWeight: 700, textAlign:"center",
        }}>
          {s.statLabel}
        </div>
        {s.subtext && (
          <div style={{opacity: subtextP, marginTop: 8}}>
            <KineticText
              text={s.subtext} mode="wave" delay={90}
              fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED}
              textAlign="center"
            />
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneCompare: React.FC<{s: Extract<CafeScene,{type:"compare"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const vsP = spring({frame: frame - 50, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="gradient-drift" accent={GREEN} accent2={IDV.error}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
        gap: 16, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        <CompareCard title={s.labelA} subtitle={s.subtitleA} color={GREEN} icon={s.iconA} delay={10} highlight/>
        <div style={{opacity: vsP, fontSize: FONT_SCALE.label.min, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK}}>vs</div>
        <CompareCard title={s.labelB} subtitle={s.subtitleB} color={IDV.error} icon={s.iconB} delay={55}/>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneList: React.FC<{s: Extract<CafeScene,{type:"list"}>; ttsVol: number}> = ({s, ttsVol}) => (
  <AbsoluteFill>
    <SceneBackground mode="dark-void" accent={ACCENT}/>
    <AbsoluteFill style={{justifyContent:"center", alignItems:"flex-start", flexDirection:"column",
      gap: 28, padding: `${SZ.top}px ${SZ.left + 16}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(s.audio)} volume={ttsVol}/>
      {s.pill && <Pill text={s.pill} color={ACCENT} delay={5}/>}
      <KineticText
        text={s.title} mode="stagger-up" delay={15}
        fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
        highlightWords={s.highlightWords ?? []} highlightColor={ACCENT}
        textAlign="left" lineHeight={1.2} maxWidth="95%"
      />
      <div style={{display:"flex", flexDirection:"column", gap: 20, width:"100%"}}>
        {s.bullets.map((b, i) => (
          <BulletItem key={i} text={b} delay={30 + i * 20} accent={ACCENT}/>
        ))}
      </div>
    </AbsoluteFill>
  </AbsoluteFill>
);

const SceneCommitment: React.FC<{s: Extract<CafeScene,{type:"commitment"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const bodyP = spring({frame: frame - 65, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="dark-void" accent={ACCENT}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"flex-start", flexDirection:"column",
        gap: 24, padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        {s.pill && <Pill text={s.pill} color={ACCENT} delay={5} icon="⚡"/>}
        <KineticText
          text={s.title} mode="split-reveal" delay={15}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightColor={ACCENT} textAlign="left" lineHeight={1.2} maxWidth="92%"
        />
        {s.body && (
          <div style={{opacity: bodyP, transform: `translateY(${interpolate(bodyP,[0,1],[12,0],CLAMP)}px)`}}>
            <KineticText
              text={s.body} mode="stagger-blur" delay={70}
              fontSize={FONT_SCALE.body.min} fontWeight={500} color={MUTED}
              textAlign="left" maxWidth="92%"
            />
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const ScenePayoff: React.FC<{s: Extract<CafeScene,{type:"payoff"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const subtitleP = spring({frame: frame - 80, fps, config: SPRINGS.smooth});
  return (
    <AbsoluteFill>
      <SceneBackground mode="warm-glow" accent={ACCENT}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
        gap: 28, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        {s.pill && <Pill text={s.pill} color={ACCENT} delay={5} icon="🎯"/>}
        <KineticText
          text={s.title} mode="wave" delay={15}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightWords={s.highlightWords ?? []} highlightColor={ACCENT}
          textAlign="center" lineHeight={1.2} maxWidth="90%"
        />
        {s.subtitle && (
          <div style={{opacity: subtitleP, transform: `translateY(${interpolate(subtitleP,[0,1],[16,0],CLAMP)}px)`}}>
            <KineticText
              text={s.subtitle} mode="stagger-up" delay={85}
              fontSize={FONT_SCALE.body.min} fontWeight={600} color={ACCENT}
              textAlign="center"
            />
          </div>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const SceneCta: React.FC<{s: Extract<CafeScene,{type:"cta"}>; ttsVol: number}> = ({s, ttsVol}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 90, fps, config: SPRINGS.snappy});
  return (
    <AbsoluteFill>
      <SceneBackground mode="warm-glow" accent={GREEN}/>
      <AbsoluteFill style={{justifyContent:"center", alignItems:"center", flexDirection:"column",
        gap: 28, padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
        <Audio src={staticFile(s.audio)} volume={ttsVol}/>
        <KineticText
          text={s.question} mode="split-reveal" delay={10}
          fontSize={FONT_SCALE.h2.min} fontWeight={700} color={WHITE}
          highlightColor={ACCENT} textAlign="center" lineHeight={1.2} maxWidth="90%"
        />
        <div style={{
          opacity: profileP, display:"flex", alignItems:"center", gap: 18,
          transform: `translateY(${interpolate(profileP,[0,1],[20,0],CLAMP)}px)`,
        }}>
          <div style={{
            width: 60, height: 60, borderRadius:"50%", overflow:"hidden",
            border: `3px solid ${GREEN}`, boxShadow: `0 0 24px ${GREEN}40`,
            background: "#111",
          }}>
            <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
              style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
          <div>
            <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ── Transition picker por posição ─────────────────────────────────────────────
// Rotação de transições — todas tipadas como fade para TypeScript aceitar
const TRANS_FNS = [fade, fade, slide, fade] as const;

// ── Main ──────────────────────────────────────────────────────────────────────
export const CafeReel: React.FC<z.infer<typeof cafeReelSchema>> = ({scenes, bgm, bgmVolume, ttsVolume}) => {
  const bgmConf = SERIES_BGM["cafe-com-teu-roi"];
  const duckSegs = scenesToDuckSegments(scenes.map(s=>({dur:s.durFrames,file:s.audio})), 30);

  const renderScene = (s: CafeScene, i: number) => {
    switch (s.type) {
      case "hook":       return <SceneHook s={s} ttsVol={ttsVolume} key={i}/>;
      case "concept":    return <SceneConcept s={s} ttsVol={ttsVolume} key={i}/>;
      case "stat":       return <SceneStat s={s} ttsVol={ttsVolume} key={i}/>;
      case "compare":    return <SceneCompare s={s} ttsVol={ttsVolume} key={i}/>;
      case "list":       return <SceneList s={s} ttsVol={ttsVolume} key={i}/>;
      case "commitment": return <SceneCommitment s={s} ttsVol={ttsVolume} key={i}/>;
      case "payoff":     return <ScenePayoff s={s} ttsVol={ttsVolume} key={i}/>;
      case "cta":        return <SceneCta s={s} ttsVol={ttsVolume} key={i}/>;
    }
  };

  return (
    <AbsoluteFill style={{backgroundColor:"#000"}}>
      {/* BGM com auto-ducking durante TTS */}
      <AudioTrack
        src={staticFile(bgm)}
        volume={bgmVolume}
        duckDuringSegments={duckSegs}
        duckVolume={bgmConf.duckVolume}
        fadeInDurationSeconds={bgmConf.fadeInSeconds}
        fadeOutDurationSeconds={bgmConf.fadeOutSeconds}
        loop
      />

      {/* TransitionSeries — transições reais (fade/slide/wipe) entre cenas */}
      <TransitionSeries>
        {scenes.map((s, i) => (
          <React.Fragment key={i}>
            <TransitionSeries.Sequence durationInFrames={s.durFrames}>
              {renderScene(s, i)}
            </TransitionSeries.Sequence>
            {i < scenes.length - 1 && (
              <TransitionSeries.Transition
                presentation={TRANS_FNS[i % TRANS_FNS.length]()}
                timing={linearTiming({durationInFrames: TRANS})}
              />
            )}
          </React.Fragment>
        ))}
      </TransitionSeries>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
